/**
 * Currency Utilities
 * Provides currency-related functions for the application
 */

/**
 * List of available currencies with their codes, names, and symbols
 */
export const AVAILABLE_CURRENCIES = [
  { code: 'EUR', name: 'Euro', symbol: '€', symbolPosition: 'suffix' }, // 100€
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', symbolPosition: 'prefix' }, // R$100
  { code: 'USD', name: 'US Dollar', symbol: '$', symbolPosition: 'prefix' }, // $100
  { code: 'GBP', name: 'British Pound', symbol: '£', symbolPosition: 'prefix' }, // £100
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', symbolPosition: 'prefix' }, // C$100
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', symbolPosition: 'prefix' }, // A$100
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', symbolPosition: 'prefix' }, // ¥100
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', symbolPosition: 'suffix' }, // 100 CHF
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', symbolPosition: 'prefix' }, // ¥100
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', symbolPosition: 'prefix' }, // ₹100
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', symbolPosition: 'prefix' }, // $100
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', symbolPosition: 'prefix' }, // S$100
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', symbolPosition: 'prefix' }, // HK$100
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', symbolPosition: 'suffix' }, // 100 kr
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', symbolPosition: 'suffix' }, // 100 kr
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', symbolPosition: 'suffix' }, // 100 kr
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', symbolPosition: 'suffix' }, // 100 zł
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', symbolPosition: 'prefix' }, // ₽100
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', symbolPosition: 'prefix' }, // ₺100
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', symbolPosition: 'prefix' }, // R100
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', symbolPosition: 'prefix' }, // NZ$100
  { code: 'ARS', name: 'Argentine Peso', symbol: '$', symbolPosition: 'prefix' } // $100
];

/**
 * Default currency code
 */
export const DEFAULT_CURRENCY = 'EUR';

/**
 * Get currency by code
 * @param {string} code - Currency code (e.g., 'USD', 'EUR')
 * @returns {Object|null} Currency object or null if not found
 */
export const getCurrencyByCode = (code) => {
  if (!code) {
    return null;
  }

  const normalizedCode = String(code).toUpperCase();
  return AVAILABLE_CURRENCIES.find((currency) => currency.code === normalizedCode) || null;
};

/**
 * Get currency name in dropdown format (e.g., "EUR (€)")
 * @param {string} code - Currency code
 * @returns {string} Formatted currency name
 */
export const getCurrencyDisplayName = (code) => {
  const currency = getCurrencyByCode(code);
  if (!currency) return code;
  return `${currency.code} (${currency.symbol})`;
};

/**
 * Get currency symbol by code
 * @param {string} code - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (code) => {
  const currency = getCurrencyByCode(code);
  return currency ? currency.symbol : code;
};

const formatAmountEuropean = (amount, options = {}) => {
  const {
    decimals = 2,
    showDecimals = true,
    thousandSeparator = true,
  } = options;

  const safeAmount = Number.isFinite(Number(amount)) ? Number(amount) : 0;

  let fractionDigits = decimals;
  if (!showDecimals && safeAmount % 1 === 0) {
    fractionDigits = 0;
  }

  const formatted = safeAmount.toFixed(fractionDigits);
  const [integerPart, decimalPart] = formatted.split('.');
  let groupedInteger = integerPart;

  if (thousandSeparator) {
    groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  if (fractionDigits === 0 || !decimalPart) {
    return groupedInteger;
  }

  return `${groupedInteger},${decimalPart}`;
};

/**
 * Format amount with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {Object} options - Formatting options
 * @returns {string} Formatted string (e.g., "12.594,50€" or "R$12.594,50")
 */
export const formatCurrency = (amount, currencyCode, options = {}) => {
  const currency = getCurrencyByCode(currencyCode);
  const safeAmount = Number(amount);

  if (!currency) {
    return formatAmountEuropean(Number.isFinite(safeAmount) ? safeAmount : 0, options);
  }

  const formattedAmount = formatAmountEuropean(
    Number.isFinite(safeAmount) ? safeAmount : 0,
    options,
  );

  if (currency.symbolPosition === 'prefix') {
    return `${currency.symbol}${formattedAmount}`;
  }

  return `${formattedAmount}${currency.symbol}`;
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string (e.g., "$100.00" or "€100,00")
 * @param {string} currencyCode - Currency code
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString, currencyCode) => {
  const currency = getCurrencyByCode(currencyCode);
  if (!currency) return parseFloat(currencyString);

  let cleaned = String(currencyString || '').replace(currency.symbol, '').trim();
  cleaned = cleaned.replace(/\s/g, '');

  if (cleaned.includes(',')) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    cleaned = cleaned.replace(/\./g, '');
  }

  return parseFloat(cleaned) || 0;
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @param {number} exchangeRate - Exchange rate (fromCurrency to toCurrency)
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRate) => {
  if (fromCurrency === toCurrency) return amount;
  return amount * exchangeRate;
};

/**
 * Get all currency codes
 * @returns {string[]} Array of currency codes
 */
export const getAllCurrencyCodes = () => {
  return AVAILABLE_CURRENCIES.map(currency => currency.code);
};

/**
 * Get currency options for dropdowns
 * @returns {Array} Array of objects with value and label for dropdowns
 */
export const getCurrencyOptions = () => {
  return AVAILABLE_CURRENCIES.map(currency => ({
    code: currency.code,
    name: getCurrencyDisplayName(currency.code),
    symbol: currency.symbol
  }));
};

/**
 * Validate currency code
 * @param {string} code - Currency code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidCurrencyCode = (code) => {
  return AVAILABLE_CURRENCIES.some(currency => currency.code === code);
};

/**
 * Get currencies grouped by common regions
 * @returns {Object} Grouped currencies
 */
export const getCurrenciesByRegion = () => {
  return {
    'Popular': ['USD', 'EUR', 'GBP', 'JPY'],
    'North America': ['USD', 'CAD', 'MXN'],
    'South America': ['BRL', 'ARS'],
    'Europe': ['EUR', 'GBP', 'CHF', 'NOK', 'SEK', 'DKK', 'PLN', 'RUB'],
    'Asia Pacific': ['JPY', 'CNY', 'INR', 'SGD', 'HKD', 'AUD', 'NZD'],
    'Middle East & Africa': ['TRY', 'ZAR', 'SAR', 'AED']
  };
};

